import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { motion } from 'framer-motion';
import { useInView } from '../../hooks/useInView';
import { useSEO } from '../../utils/seo';

const AboutPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://goglobalworld.net';
  
  useSEO({
    title: 'About Go Global Inc. | Direct Import Construction Materials Supplier | Eliminate Middleman Costs',
    description: 'Go Global Inc. connects custom home builders, real estate developers, and contractors with factory-direct construction materials. Save 20-40% on wholesale building supplies through our direct import model. 5,000+ verified global manufacturers, expert logistics, transparent pricing. Serving Canada with bulk construction materials and steel building packages.',
    keywords: 'wholesale construction materials supplier, direct import building materials, factory direct construction supply, bulk building materials, construction cost savings, steel building supplier Canada',
    canonical: `${siteUrl}/about`,
    ogImage: 'https://public.readdy.ai/ai/img_res/0bfb3a36289d3900563c9f04d4c92881.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Go Global Inc.',
      description: 'Direct import wholesale construction materials supplier eliminating middleman markup for builders and developers',
      mainEntity: {
        '@type': 'Organization',
        name: 'Go Global Inc.',
        description: 'Factory-direct construction materials supplier serving custom home builders, real estate developers, renovation contractors, and property investors across Canada',
        foundingDate: '2020',
        url: siteUrl,
      }
    },
  });

  const [heroRef, heroInView] = useInView({ threshold: 0.2 });
  const [storyRef, storyInView] = useInView({ threshold: 0.2 });
  const [networkRef, networkInView] = useInView({ threshold: 0.2 });

  const stats = [
    { number: '2014', label: 'Founded', sublabel: '30+ Years Combined Experience' },
    { number: '50+', label: 'Team Members', sublabel: 'Global Experts' },
    { number: '5,000+', label: 'Supplier Network', sublabel: 'Verified Partners' },
    { number: '40+', label: 'Countries', sublabel: 'Operating Regions' },
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Assurance',
      description: 'Rigorous supplier vetting and quality control processes ensure every product meets international standards.',
    },
    {
      icon: 'ri-global-line',
      title: 'Global Reach',
      description: 'Established partnerships across continents enable us to source and deliver anywhere in the world.',
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Client-Focused',
      description: 'Dedicated account management and 24/7 support ensure your project stays on track.',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Innovation Driven',
      description: 'Continuously evolving our platform and processes to deliver better outcomes for our partners.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        {/* Hero Section */}
        <section ref={heroRef} className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <div className="text-xs font-semibold text-primary-600 tracking-widest uppercase mb-6">
                ABOUT GO GLOBAL INC.
              </div>
              <h1 className="font-display font-light text-5xl md:text-6xl lg:text-7xl text-gray-900 leading-tight mb-8">
                Building the Future <br />
                of Construction <br />
                <span className="font-semibold">Supply Chains</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                With over 30 years of combined industry experience, we're transforming how construction projects 
                source, procure, and receive materials from around the world.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white border-y border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="text-center"
                >
                  <div className="font-display font-bold text-4xl md:text-5xl bg-gradient-to-r from-primary-600 to-secondary-600 text-transparent bg-clip-text mb-2">
                    {stat.number}
                  </div>
                  <div className="text-sm font-semibold text-gray-900">{stat.label}</div>
                  <div className="text-xs text-gray-600 mt-1">{stat.sublabel}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Company Story */}
        <section ref={storyRef} className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              {/* Main Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={storyInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="lg:col-span-3 space-y-6 text-lg text-gray-700 leading-relaxed"
              >
                <h2 className="font-display font-semibold text-3xl text-gray-900 mb-6">Our Story</h2>
                
                <p>
                  Go Global Inc. was founded on a simple observation: construction projects worldwide face 
                  unnecessary complexity, cost, and delays when sourcing materials internationally. With decades 
                  of combined experience in construction, logistics, and international trade, our team set out 
                  to build a better solution.
                </p>

                <p>
                  Today, we serve as a trusted partner to developers, general contractors, and investors across 
                  North America, South America, the Caribbean, and Africa. Our network of over 5,000 verified 
                  manufacturing partners enables us to source virtually any construction material at wholesale 
                  pricing while maintaining strict quality standards.
                </p>

                <p>
                  What sets us apart is our end-to-end approach. We don't just connect you with suppliers—we 
                  manage the entire supply chain from factory pickup to jobsite delivery. Our in-house customs 
                  clearance capabilities in Canada and the United States, combined with documentation support 
                  for international shipments, eliminate the headaches typically associated with global sourcing.
                </p>

                <p>
                  As we continue to grow, our vision extends beyond traditional supply chain services. We're 
                  building a comprehensive platform that will further streamline construction procurement, 
                  making it easier than ever for projects of all sizes to access global markets efficiently 
                  and cost-effectively.
                </p>
              </motion.div>

              {/* Sidebar */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={storyInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="lg:col-span-2 space-y-6"
              >
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h3 className="font-display font-semibold text-xl text-gray-900 mb-4">Our Mission</h3>
                  <p className="text-gray-700 leading-relaxed">
                    To simplify global construction supply chains by providing direct factory access, 
                    comprehensive logistics support, and unmatched customer service.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h3 className="font-display font-semibold text-xl text-gray-900 mb-4">Our Vision</h3>
                  <p className="text-gray-700 leading-relaxed">
                    To become the world's most trusted construction supply partner, enabling projects 
                    everywhere to build better, faster, and more cost-effectively.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl p-6 text-white">
                  <h3 className="font-display font-semibold text-xl mb-4">Geographic Reach</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center">
                      <i className="ri-check-line mr-2"></i>
                      Any Location in North America
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line mr-2"></i>
                      Any Safe Port Worldwide
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section ref={networkRef} className="py-20 lg:py-28 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={networkInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-display font-light text-4xl md:text-5xl text-gray-900 mb-4">
                Our <span className="font-semibold">Core Values</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                The principles that guide everything we do
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={networkInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl mb-4">
                    <i className={`${value.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="font-display font-semibold text-lg text-gray-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;